// Блок фильтров, который на телефоне сворачивается.
//
// ЗАЧЕМ. Фильтров в разделе пять-шесть, на узком экране они выстраиваются в столбец и
// занимают 600–700 пикселей — первая строка списка оказывается за пределами экрана всегда.
// Человек открывает «Дебиторку» и видит поля ввода, а не долги.
//
// ЧЕГО НЕЛЬЗЯ ДОПУСТИТЬ, сворачивая. Свёрнутый фильтр — это скрытая причина: список урезан,
// а почему — не видно, и «долгов нет» читается как факт. Поэтому заголовок свёрнутого блока
// ВСЕГДА перечисляет, что применено, и подсвечивается, когда применено хоть что-то.
//
// ОТКУДА БЕРЁТСЯ ПЕРЕЧИСЛЕНИЕ. Из самих полей, а не из списка, который передала страница.
// Причина та же, по которой подписи колонок в PaymentsTable считаются один раз: две копии
// разъезжаются. Здесь копия разъехалась бы молча — в заголовке «Стадия: Выиграна», а в поле
// уже другое. Поэтому после каждой отрисовки блок перечитывает свои же поля: подпись берётся
// из <label>, значение — из того, что в поле выбрано. Придумать своё название фильтру этот
// код не может физически, и это главное его свойство.
//
// НА МОНИТОРЕ НЕ МЕНЯЕТСЯ НИЧЕГО. Разметка та же, что была (`div.filters` с полями внутри),
// плюс кнопка, которую CSS показывает только на ≤700px (styles.css, §12). Сворачивание —
// это `display: none` у полей, а не размонтирование: значения фильтров живут в состоянии
// страницы и в адресе, а набранный в поиске сущности текст — внутри самого поля, и от
// сворачивания он никуда не девается.
import { useCallback, useEffect, useRef, useState } from 'react'

import { fmtDate } from '../lib/format'

interface Props {
  /**
   * Ключ, под которым запоминается «свёрнут/раскрыт». Разный у каждого блока: в разделе
   * их бывает несколько (вкладки закупок, отчёты руководителя), и общий ключ схлопывал бы
   * их все разом. Менять нельзя — сбросит выбор людям.
   */
  id: string
  children: React.ReactNode
  /** Отступы, которые страница задавала блоку фильтров. Идут на обёртку. */
  style?: React.CSSProperties
}

/** Где помним, какие блоки фильтров человек оставил раскрытыми. */
const OPEN_KEY = 'cir-filters-open'

type OpenMap = Record<string, boolean>

function loadOpen(): OpenMap {
  try {
    const raw = localStorage.getItem(OPEN_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    if (parsed && typeof parsed === 'object') return parsed as OpenMap
  } catch {
    // Приватный режим Safari бросает на localStorage. Фильтры важнее памяти о них.
  }
  return {}
}

/**
 * Свёрнут по умолчанию: ради этого всё и затевалось. Раскрыл человек — помним.
 * На мониторе значение ни на что не влияет, поля там видны всегда.
 */
const openFor = (id: string, saved: OpenMap): boolean => saved[id] ?? false

export function FilterBar({ id, children, style }: Props) {
  const боксRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(() => openFor(id, loadOpen()))
  const [сводка, setСводка] = useState<Значение[]>([])

  const пересчитать = useCallback(() => {
    const поля = боксRef.current ? заполненные(боксRef.current) : []
    // Сравнение обязательно: пересчёт идёт после КАЖДОЙ отрисовки, и новый массив с тем же
    // содержимым запускал бы отрисовку заново — до бесконечности.
    setСводка((было) => (было.length === поля.length
      && было.every((з, i) => з.текст === поля[i].текст && з.сужает === поля[i].сужает)
      ? было
      : поля))
  }, [])

  // Без списка зависимостей намеренно: значения полей приходят из состояния страницы, и
  // единственный надёжный признак «что-то могло измениться» — сама отрисовка.
  useEffect(пересчитать)

  const переключить = (next: boolean) => {
    setOpen(next)
    try {
      localStorage.setItem(OPEN_KEY, JSON.stringify({ ...loadOpen(), [id]: next }))
    } catch {
      // не смогли запомнить — не повод не сворачивать
    }
  }

  // Подсвечиваем не «что-то заполнено», а «выборка сужена». Разница живая: на экране
  // аналитики период и воронка заполнены всегда («Год», «Все воронки»), и подсветка по
  // факту заполнения горела бы постоянно — то есть не значила бы ничего. Перечисляем при
  // этом всё заполненное: знать, за какой период смотришь, нужно и когда фильтр нейтрален.
  const сужено = сводка.some((з) => з.сужает)

  return (
    <div className="filter-box" data-open={open} data-applied={сужено} style={style}>
      <button
        type="button"
        className="filter-toggle"
        aria-expanded={open}
        aria-controls={`filters-${id}`}
        onClick={() => переключить(!open)}
      >
        <span>Фильтры</span>
        <span className="chev" aria-hidden="true">▾</span>
        {/* Перечисление применённого — часть кнопки, а не строка рядом: на телефоне это
            единственное, что видно от фильтров, и оно должно читаться вместе с ними. */}
        <span className="filter-sum">
          {сводка.length ? сводка.map((з) => з.текст).join(' · ') : 'не заданы'}
        </span>
      </button>

      {/* onChange ловит правку поля, которая не дошла до состояния страницы (поле само
          хранит своё значение). Отрисовка такую правку не вызывает, а сводка обязана
          обновиться: иначе в заголовке останется прежнее значение. */}
      <div className="filters" id={`filters-${id}`} ref={боксRef} onChange={пересчитать}>
        {children}
      </div>
    </div>
  )
}

// ─────────────────────────── что заполнено, словами полей ───────────────────────────

/** Одно заполненное поле фильтра. */
interface Значение {
  /** «Стадия: Выиграна» — ровно то, что видно в самом поле. */
  текст: string
  /**
   * Сужает ли это значение выборку. У выпадающего списка первый пункт всегда нейтральный
   * («любая», «Все воронки», «— не выбрано —») — на нём список полон, и подсвечивать нечего.
   */
  сужает: boolean
}

/** Поле ввода, из которого можно прочитать значение. */
type Поле = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

/** Типы, у которых «значение» — не фильтр: кнопки, файлы, скрытое. */
const НЕ_ФИЛЬТР = new Set(['hidden', 'button', 'submit', 'reset', 'image', 'file'])

/**
 * Перечень заполненных полей в том порядке, в каком они стоят на экране.
 *
 * Пустое значение не показываем вовсе: у выпадающих списков CRM нейтральный пункт обычно
 * имеет пустое значение, у полей ввода пусто значит «не сужаем».
 */
function заполненные(бокс: HTMLElement): Значение[] {
  const out: Значение[] = []
  // Один запрос на оба вида, чтобы поля шли в порядке разметки: `.picked` — это выбранная
  // сущность в поиске (EntityPicker), у неё поля ввода уже нет.
  for (const узел of Array.from(бокс.querySelectorAll('input, select, textarea, .picked'))) {
    const з = узел.classList.contains('picked')
      ? выбраннаяСущность(узел as HTMLElement)
      : значениеПоля(узел as Поле, бокс)
    if (з) out.push(з)
  }
  return out
}

/** Выбранная сущность: «Компания: ООО „Норд“». Выбрана — значит выборка сужена. */
function выбраннаяСущность(picked: HTMLElement): Значение | null {
  const название = picked.querySelector('.picked-title')?.textContent?.trim() ?? ''
  if (!название) return null
  return { текст: собрать(подписьБлока(picked), название), сужает: true }
}

function значениеПоля(поле: Поле, бокс: HTMLElement): Значение | null {
  if (поле instanceof HTMLInputElement && НЕ_ФИЛЬТР.has(поле.type)) return null
  // Поле поиска сущности: набранный текст ещё не фильтр — им станет выбранная строка,
  // а она приезжает как `.picked` и учитывается отдельно.
  if (поле.getAttribute('role') === 'combobox') return null

  const подпись = подписьПоля(поле, бокс)

  if (поле instanceof HTMLInputElement && (поле.type === 'checkbox' || поле.type === 'radio')) {
    // Галочка сама себе значение: «только открытые» читается как есть, «Только открытые: да»
    // — нет. Подпись у неё всегда рядом, потому что цель нажатия — вся подпись целиком.
    return поле.checked ? { текст: подпись, сужает: true } : null
  }

  if (поле instanceof HTMLSelectElement) {
    // Показываем то, что видит человек, а не код: «C4:WON» ответом на вопрос «какие фильтры
    // применены» не является.
    const значение = (поле.selectedOptions[0]?.textContent ?? '').trim()
    if (!значение || !поле.value) return null
    return { текст: собрать(подпись, значение), сужает: поле.selectedIndex > 0 }
  }

  const значение = поле.value.trim()
  if (!значение) return null
  return {
    текст: собрать(подпись, поле instanceof HTMLInputElement && поле.type === 'date'
      // Поле даты показывает дату по-русски, а хранит «2026-06-01». В заголовке должно
      // стоять то же, что человек видит в поле.
      ? fmtDate(значение)
      : значение),
    сужает: true,
  }
}

const собрать = (подпись: string, значение: string): string =>
  (подпись ? `${подпись}: ${значение}` : значение)

/**
 * Подпись поля — ровно та, что стоит над ним на экране. Своих слов здесь нет и быть не
 * может: раздел переименует фильтр, и заголовок переименуется вместе с ним.
 */
function подписьПоля(поле: Поле, бокс: HTMLElement): string {
  const своя = поле.closest('label')
  if (своя) return чисто(своя.textContent)
  if (поле.id) {
    const метка = Array.from(бокс.querySelectorAll('label')).find((l) => l.htmlFor === поле.id)
    if (метка) return чисто(метка.textContent)
  }
  return чисто(поле.getAttribute('aria-label'))
}

/** Подпись блока поля: у выбранной сущности своего <label for> нет. */
const подписьБлока = (узел: HTMLElement): string =>
  чисто(узел.closest('.f')?.querySelector('label')?.textContent)

/** Двоеточие в конце подписи своё мы добавим сами, поэтому чужое снимаем. */
const чисто = (v?: string | null): string => (v ?? '').replace(/\s+/g, ' ').trim().replace(/:$/, '')
